/* eslint-disable no-param-reassign */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, map } from 'underscore';
import classnames from 'classnames';
import {
  DefaultButton,
  Label,
  IconButton,
  TextField,
  PrimaryButton,
  Panel,
  PanelType,
  Dropdown,
  Icon,
} from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, withValidation } from '@bingads-webui-react/validation';
import {
  extractExclusionRules,
  withHttpsPrefix,
  keywordsToCriterionName,
  convertURLEqualsToCriterion,
  convertURLContainsToCriterion,
  convertCustomLabelToCriterion,
} from '../helpers/utils';
import { EXCLUSTION_TYPE } from '../helpers/constants';
import { ValidatedUrlInput } from './validated-url-input';
import { TagWithTooltip } from './tag-with-tooltip';

const ValidatedInput = withValidation(TextField);
const ValidationSchema = {
  name: 'Exclusion Input',
  type: 'object',
  properties: {
    rule: {
      type: 'string',
      maxLength: 2048,
    },
    label: {
      type: 'string',
      maxLength: 2048,
    },
  },
};

export const getFinalURLSectionStyle = (_, { palette = {} }) => ({
  inputContainer: {
    display: 'flex',
  },
  dropdown: {
    minWidth: '161px', height: '32px', marginRight: '8px',
  },
  dropdownOption: {
    display: 'flex',
  },
  iconStyles: {
    marginRight: '8px',
  },
  textField: {
    width: '100%', height: '32px',
  },
  addButton: {
    width: '60px', height: '32px', float: 'right', margin: '8px 0px 8px 8px ',
  },
  sectionsContainer: {
    '& label': {
      fontSize: 18,
    },
    '& .footerButton': {
      marginLeft: '8px',
    },
    '& .description': { marginTop: '12px', marginBottom: '12px' },
    '& .divider': {
      marginTop: '37px',
      '& .final-urls-section:last-child': { borderBottom: 'none' },
    },
    '& .final-urls-section': {
      margin: '2px 0',
      borderBottom: '1px solid #EDEBE9',
    },
    '& .dimension': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4px',
      margin: '8px 0 0 0',
      padding: '0 0 16px 0',
      '& .dimensionItem': {
        alignItems: 'normal',
        backgroundColor: '#F3F2F1',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'row',
        gap: '6px',
        fontWeight: 600,
        padding: '4px 0px 4px 10px',
        height: '30px',
        '& button': {
          color: palette.neutralSecondary,
          height: 'auto',
          '& .ms-Icon': {
            fontSize: 12,
          },
        },
        '& .tagText': {
          maxWidth: '488px',
          overflow: 'hidden',
          textWrap: 'nowrap',
          textOverflow: 'ellipsis',
        },
      },
    },
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .error': {
      color: '#A80000',
    },
  },
});

export const FinalURLsEditor = (props) => {
  const {
    i18n,
    isOpen,
    onClose,
    onChange,
    finalUrlExpansions,
  } = props;

  const classes = useDefaultStyles(getFinalURLSectionStyle);

  const [newFinalUrlExpansions, setNewFinalUrlExpansions] = useState(finalUrlExpansions);
  const [urls, rules, labels] = useMemo(() => extractExclusionRules(newFinalUrlExpansions), [newFinalUrlExpansions]);
  const [selectedOption, setSelectedOption] = useState(EXCLUSTION_TYPE.URL);
  const [input, setInput] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  const onAddTag = useCallback(() => {
    if (!input) return;
    if (selectedOption === EXCLUSTION_TYPE.URL) {
      const newUrl = withHttpsPrefix(input);
      if (!urls.includes(newUrl)) {
        setNewFinalUrlExpansions([...newFinalUrlExpansions, convertURLEqualsToCriterion(newUrl)]);
      }
    } else if (selectedOption === EXCLUSTION_TYPE.Rule) {
      // Current UI doesn't support multiple AND for a single URL condition so just ignore the case.
      const flattenRules = rules.filter(rule => rule.length === 1).flat();
      if (!flattenRules.includes(input)) {
        setNewFinalUrlExpansions([...newFinalUrlExpansions, convertURLContainsToCriterion(input)]);
      }
    } else if (selectedOption === EXCLUSTION_TYPE.Label) {
      if (!labels.includes(input)) {
        setNewFinalUrlExpansions([...newFinalUrlExpansions, convertCustomLabelToCriterion(input)]);
      }
    }
    setInput('');
  }, [input, setInput, urls, rules, labels, selectedOption, newFinalUrlExpansions]);

  const onRemovetag = useCallback((value, id, seletedGroup) => {
    if (seletedGroup === EXCLUSTION_TYPE.Rule) {
      const target = keywordsToCriterionName(value);
      setNewFinalUrlExpansions(newFinalUrlExpansions.filter((exclusion) => {
        const criterionName = get(exclusion, ['Criterion', 'WebPage', 'Parameter', 'CriterionName'], '');
        return criterionName !== target;
      }));
    } else {
      setNewFinalUrlExpansions(newFinalUrlExpansions.filter((exclusion) => {
        const { ArgumentOperand } = get(exclusion, ['Criterion', 'WebPage', 'Parameter', 'Conditions', 0], {});
        return ArgumentOperand !== value;
      }));
    }
  }, [newFinalUrlExpansions]);

  const onSave = useCallback(() => {
    onChange({ finalUrlExpansions: newFinalUrlExpansions });
    onClose();
  }, [newFinalUrlExpansions, onChange, onClose]);

  const options = [
    { key: EXCLUSTION_TYPE.URL, text: i18n.getString(_TL_('URLs')), data: { icon: 'Accept' } },
    { key: EXCLUSTION_TYPE.Rule, text: i18n.getString(_TL_('URL contains')), data: { icon: 'Accept' } },
    { key: EXCLUSTION_TYPE.Label, text: i18n.getString(_TL_('Custom labels')), data: { icon: 'Accept' } },
  ];

  const renderInputField = useCallback(() => {
    switch (selectedOption) {
      case EXCLUSTION_TYPE.URL:
        return (
          <ValidatedUrlInput
            className={classnames('auto-generated-assets-url-input', classes.textField)}
            i18n={i18n}
            placeholder={i18n.getString(_TL_('Enter or paste the URL that should be excluded'))}
          />
        );
      case EXCLUSTION_TYPE.Rule:
        return (
          <ValidatedInput
            className={classnames('auto-generated-assets-rule-input', classes.textField)}
            placeholder={i18n.getString(_TL_('Create rules to exclude'))}
            validationProps={{
              field: 'rule',
            }}
            value={input}
          />
        );
      case EXCLUSTION_TYPE.Label:
        return (
          <ValidatedInput
            className={classnames('auto-generated-assets-custom-label-input', classes.textField)}
            placeholder={i18n.getString(_TL_('Labels from page feeds to exclude'))}
            validationProps={{
              field: 'label',
            }}
            value={input}
          />
        );
      default:
        return null;
    }
  }, [classes, i18n, selectedOption, input]);

  const renderUrlTags = useCallback(() => (
    <div className="final-urls-section">
      <Label>{i18n.getString(_TL_('URLs'))}</Label>
      <div>{i18n.getString(_TL_('URLs that should be excluded'))}:</div>
      <div className="dimension">
        { map(urls, (url, index) => (
          <div className={classnames('auto-generated-assets-tag', 'dimensionItem')} key={`urls-${url}`}>
            <TagWithTooltip className="auto-generated-assets-url-tag-value" content={url} />
            <IconButton
              className={classnames('auto-generated-assets-url-remove-btn', 'dimensionButton')}
              aria-label={i18n.getString(_TL_('Remove {{url}}'), { url })}
              iconProps={{ iconName: 'Cancel' }}
              onClick={() => onRemovetag(url, index, EXCLUSTION_TYPE.URL)}
            />
          </div>
        )) }
      </div>
    </div>
  ), [i18n, urls, onRemovetag]);

  const renderRuleTags = useCallback(() => (
    <div className="final-urls-section">
      <Label>{i18n.getString(_TL_('URL Contains'))}</Label>
      <div>{i18n.getString(_TL_('Rules to exclude non-commercial pages or sections of your website'))}:</div>
      <div className="dimension">
        {
          map(rules, (rule, index) => {
            const tag = keywordsToCriterionName(rule);
            return (
              <div className={classnames('auto-generated-assets-tag', 'dimensionItem')} key={tag}>
                <TagWithTooltip className="auto-generated-assets-url-tag-value" content={tag} />
                <IconButton
                  className={classnames('auto-generated-assets-rule-remove-btn', 'dimensionButton')}
                  aria-label={i18n.getString(_TL_('Remove {{tag}}'), { tag })}
                  iconProps={{ iconName: 'Cancel' }}
                  onClick={() => onRemovetag(rule, index, EXCLUSTION_TYPE.Rule)}
                />
              </div>
            );
          })
        }
      </div>
    </div>
  ), [i18n, rules, onRemovetag]);

  const renderCustomLabelTags = useCallback(() => (
    <div className="final-urls-section">
      <Label>{i18n.getString(_TL_('Custom labels'))}</Label>
      <div>{i18n.getString(_TL_('Specific which labels from page feeds to exlude'))}:</div>
      <div className="dimension">
        { map(labels, (label, index) => (
          <div className={classnames('auto-generated-assets-tag', 'dimensionItem')} key={`label-${label}`}>
            <TagWithTooltip className="auto-generated-assets-label-tag-value" content={label} />
            <IconButton
              className={classnames('auto-generated-assets-label-remove-btn', 'dimensionButton')}
              aria-label={i18n.getString(_TL_('Remove {{label}}'), { label })}
              iconProps={{ iconName: 'Cancel' }}
              onClick={() => onRemovetag(label, index, EXCLUSTION_TYPE.Label)}
            />
          </div>
        )) }
      </div>
    </div>
  ), [i18n, labels, onRemovetag]);

  const renderDropdownOptions = useCallback(option => (
    <div className={classnames('auto-generated-assets-dropdown', classes.dropdownOption)}>
      {option.data && option.data.icon && option.key === selectedOption && (
        <Icon className={classnames('auto-generated-assets-option-selected', classes.iconStyles)} iconName={option.data.icon} aria-hidden="true" title={option.data.icon} />
      )}
      <span>{option.text}</span>
    </div>
  ), [classes, selectedOption]);

  const renderPanelFooter = useCallback(() => (
    <div className="audience-editor-buttons">
      <PrimaryButton className={classnames('auto-generated-assets-apply', 'btn-save')} onClick={onSave}>
        {i18n.getString(_TL_('Apply'))}
      </PrimaryButton>
      <DefaultButton className={classnames('auto-generated-assets-cancel', 'footerButton btn-cancel')} onClick={onClose}>
        {i18n.getString(_TL_('Cancel'))}
      </DefaultButton>
    </div>
  ), [i18n, onClose, onSave]);

  const renderValidationErrors = useCallback(() => (
    <div className={classnames('auto-generated-assets-error', classes.errorContainer)}>
      { validationErrors.map(message => <span className="error" key={message}>{message}</span>) }
    </div>
  ), [classes, validationErrors]);

  const onValidate = useCallback((result) => {
    const { data, messages } = result;
    const newInput = data.input || data.rule || data.label;
    setValidationErrors(messages);
    setInput(newInput);
  }, []);

  return (
    <>
      <Panel
        className={classnames('auto-generated-assets-panel', classes.sectionsContainer)}
        headerText={i18n.getString(_TL_('Exclude some URLs'))}
        closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
        isOpen={isOpen}
        onDismiss={onClose}
        onRenderFooterContent={renderPanelFooter}
        isFooterAtBottom
        type={PanelType.medium}
      >
        <ValidationGroup
          validationProps={{
            schema: ValidationSchema,
            data: { input },
            onValidate,
          }}
        >
          <div className="description">{i18n.getString(_TL_('Enter URLs or create rules to exclude non-commercial pages or sections of your website'))}.</div>
          <div className={classes.inputContainer}>
            <Dropdown
              className={classnames('ms-Dropdown-container', classes.dropdown)}
              options={options}
              onRenderOption={renderDropdownOptions}
              selectedKey={selectedOption}
              onChange={(event, selected) => setSelectedOption(selected.key)}
            />
            { renderInputField() }
          </div>
          { renderValidationErrors() }
          <DefaultButton
            className={classnames('auto-generated-assets-panel-add-btn', 'ms-Button', classes.addButton)}
            text={i18n.getString(_TL_('Add'))}
            onClick={onAddTag}
            allowDisabledFocus
            disabled={validationErrors.length > 0}
          />
        </ValidationGroup>
        <div className="divider">
          { urls.length > 0 && renderUrlTags() }
          { rules.length > 0 && renderRuleTags() }
          { labels.length > 0 && renderCustomLabelTags() }
        </div>
      </Panel>
    </>
  );
};

FinalURLsEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  finalUrlExpansions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};
